/**
 * DiscoverRightPanelSlot
 * Self-contained component rendered by DashboardLayout when on /discover route.
 * Reads URL state directly and fetches facets independently,
 * eliminating the delayed useEffect injection pattern.
 */
import React, { useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDiscoverDeals } from '../../../hooks/useDiscoverDeals';
import {
  discoverUrlStateToSearchParams,
  normalizeIndustryOption,
} from '@bright-web-skeleton/discover-contract';
import type { RightPanelMode } from '@bright-web-skeleton/ui';
import { DesktopDiscoveryPanelAdapter } from './DesktopDiscoveryPanelAdapter';

interface DiscoverRightPanelSlotProps {
  panel: RightPanelMode;
}

/** Canonical URL serialization lives in @bright-web-skeleton/discover-contract. */
export const DiscoverRightPanelSlot: React.FC<DiscoverRightPanelSlotProps> = ({ panel }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Read committed state directly from URL (same source as DiscoverPage)
  const filters = useMemo(() => ({
    q: searchParams.get('q') || '',
    industry_ids: searchParams.getAll('industry_ids'),
    category_ids: searchParams.getAll('category_ids'),
    subcategory_ids: searchParams.getAll('subcategory_ids'),
  }), [searchParams]);

  const committedRanges = useMemo(() => ({
    priceRange: {
      min: searchParams.has('price_min') ? Number(searchParams.get('price_min')) : null,
      max: searchParams.has('price_max') ? Number(searchParams.get('price_max')) : null,
    },
    commissionRange: {
      min: searchParams.has('commission_min') ? Number(searchParams.get('commission_min')) : null,
      max: searchParams.has('commission_max') ? Number(searchParams.get('commission_max')) : null,
    },
    discountRange: {
      min: searchParams.has('discount_min') ? Number(searchParams.get('discount_min')) : null,
      max: searchParams.has('discount_max') ? Number(searchParams.get('discount_max')) : null,
    },
  }), [searchParams]);

  // Reuse the same hook DiscoverPage uses - data is shared via react state
  // This reads from the same URL params so results/facets stay in sync
  const { facetsV3, allIndustries, coverage, total } = useDiscoverDeals();

  const industryOptions = useMemo(() => {
    if (facetsV3?.industries?.length) return facetsV3.industries.map(normalizeIndustryOption);
    return allIndustries;
  }, [facetsV3?.industries, allIndustries]);

  const coverageBounds = useMemo(() => coverage ? {
    priceBounds: { min: Math.floor(coverage.min_price_amount ?? 0), max: Math.ceil(coverage.max_price_amount ?? 1000) },
    commissionBounds: { min: Math.floor(coverage.min_commission_amount ?? 0), max: Math.ceil(coverage.max_commission_amount ?? 100) },
    discountBounds: { min: Math.floor(coverage.min_discount_amount ?? 0), max: Math.ceil(coverage.max_discount_amount ?? 100) },
  } : undefined, [coverage]);

  // Atomic commit - mirrors DiscoverPage.commitFilters
  const commitFilters = useCallback((partial: Record<string, any>) => {
    const safeNum = (v: any) => (v != null && Number.isFinite(Number(v)) ? Number(v) : null);
    const resolveRange = (key: string, partialVal: any, current: number | null) =>
      key in partial ? safeNum(partialVal) : current;

    const merged = {
      q: partial.q !== undefined ? partial.q : filters.q,
      industry_ids: partial.industry_ids !== undefined ? partial.industry_ids : filters.industry_ids,
      category_ids: partial.category_ids !== undefined ? partial.category_ids : filters.category_ids,
      subcategory_ids: partial.subcategory_ids !== undefined ? partial.subcategory_ids : filters.subcategory_ids,
      price_min: resolveRange('price_min', partial.price_min, committedRanges.priceRange.min),
      price_max: resolveRange('price_max', partial.price_max, committedRanges.priceRange.max),
      commission_min: resolveRange('commission_min', partial.commission_min, committedRanges.commissionRange.min),
      commission_max: resolveRange('commission_max', partial.commission_max, committedRanges.commissionRange.max),
      discount_min: resolveRange('discount_min', partial.discount_min, committedRanges.discountRange.min),
      discount_max: resolveRange('discount_max', partial.discount_max, committedRanges.discountRange.max),
    };
    setSearchParams(discoverUrlStateToSearchParams(merged), { replace: true });
  }, [filters, committedRanges, setSearchParams]);

  const handleClearAll = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  const handleSearchCommit = useCallback((query: string) => {
    commitFilters({ q: query });
  }, [commitFilters]);

  const handleSetIndustryIds = useCallback((ids: string[]) => {
    commitFilters({ industry_ids: ids });
  }, [commitFilters]);

  const handleSetCategoryIds = useCallback((ids: string[]) => {
    commitFilters({ category_ids: ids });
  }, [commitFilters]);

  return (
    <DesktopDiscoveryPanelAdapter
      panel={panel}
      searchQuery={filters.q}
      selectedIndustryIds={filters.industry_ids}
      selectedCategoryIds={filters.category_ids}
      selectedSubcategoryIds={filters.subcategory_ids}
      facetsV3={facetsV3}
      industries={industryOptions}
      committedPriceRange={committedRanges.priceRange}
      committedCommissionRange={committedRanges.commissionRange}
      committedDiscountRange={committedRanges.discountRange}
      coverageBounds={coverageBounds}
      onCommitAll={commitFilters}
      onClearAll={handleClearAll}
      onSetIndustryIds={handleSetIndustryIds}
      onSetCategoryIds={handleSetCategoryIds}
      onSearchCommit={handleSearchCommit}
      totalResultCount={total}
    />
  );
};

export default DiscoverRightPanelSlot;
